import { useState } from 'react';
import { Button, Message, Space } from '@arco-design/web-react';
import { IconCopy, IconEye, IconEyeInvisible } from '@arco-design/web-react/icon';

/**
 * SensitiveText
 * --------------------------------
 * 标准化敏感字段（卡号 / CVV / 手机号 / 邮箱）的显隐 + 复制控件。
 *
 * 严格遵循前端设计规范：
 *   1. 默认遮蔽，仅展示掩码值
 *   2. 用户点击「眼睛」时强制走 onReveal 回调（业务侧需在此处接入二次验证）
 *   3. 解密成功后写入 plain，并允许一键复制
 *   4. 任何场景禁止把 plain 直接拼接到 DOM 上做兜底（防止 React DevTools 嗅探）
 */
export interface SensitiveTextProps {
  /** 已脱敏文本，例如 411111******1111 */
  masked: string;
  /** 解密后的明文（由父组件按需注入） */
  plain?: string;
  /** 用户点击「眼睛」时触发，需在内部完成二次验证 + 拉取明文 */
  onReveal?: () => Promise<string>;
  /** 是否允许复制（默认 true，未解密时按钮禁用） */
  copyable?: boolean;
}

export function SensitiveText({
  masked,
  plain,
  onReveal,
  copyable = true,
}: SensitiveTextProps) {
  const [revealed, setRevealed] = useState(false);
  const [innerPlain, setInnerPlain] = useState<string | undefined>(plain);
  const [loading, setLoading] = useState(false);

  const display = revealed ? innerPlain ?? plain ?? masked : masked;

  const handleToggle = async () => {
    if (revealed) {
      setRevealed(false);
      return;
    }
    if (innerPlain || plain) {
      setRevealed(true);
      return;
    }
    if (!onReveal) {
      Message.warning('未配置解密回调');
      return;
    }
    try {
      setLoading(true);
      const value = await onReveal();
      setInnerPlain(value);
      setRevealed(true);
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '解密失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const value = innerPlain ?? plain;
    if (!value) {
      Message.warning('请先点击眼睛图标解密');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      Message.success('已复制到剪贴板');
    } catch {
      Message.error('剪贴板写入失败，请手动复制');
    }
  };

  return (
    <Space>
      <span style={{ fontFamily: 'SFMono-Regular, Consolas, monospace' }}>{display}</span>
      <Button
        size="mini"
        type="text"
        loading={loading}
        icon={revealed ? <IconEyeInvisible /> : <IconEye />}
        onClick={handleToggle}
      />
      {copyable ? (
        <Button size="mini" type="text" icon={<IconCopy />} onClick={handleCopy} />
      ) : null}
    </Space>
  );
}
